// @ts-nocheck
import React, { useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';

import { fetchLegalNotices, validateLegalNotices } from 'actions/legalNotices';

import LogoPage from 'components/pages/LogoPage';
import { Focusable } from 'components/focus';

import DividedContent from 'components/common/DividedContent';
import WithLoading from 'components/common/WithLoading';

import { useFocusManager } from 'hooks/focus';
import useTracking from 'hooks/analytics';
import textToSpeechManager from 'support/tts/TextToSpeechManager';

import { getPageValues } from 'support/analytics/analyticsHelper';

import { isEmptyOrNull } from 'common/support/stringHelper';
import Button from 'components/common/Button';
import styles from './LegalNotices.module.scss';

const messages = {
  legalNoticesLabel: {
    id: 'legalNotices.label',
    description: 'Legal notices label',
  },
  privacyPolicyLabel: {
    id: 'legalNotices.privacy.label',
    description: 'Privacy policy label',
  },
  privacyPolicyUrl: {
    id: 'legalNotices.privacy.url',
    description: 'Privacy policy url',
  },
  termsOfUseLabel: {
    id: 'legalNotices.termsOfUse.label',
    description: 'Terms of use label',
  },
  termsOfUseUrl: {
    id: 'legalNotices.termsOfUse.url',
    description: 'Terms of use url',
  },
  videoServicesLabel: {
    id: 'legalNotices.videoServicePolicy.label',
    description: 'Video services label',
  },
  videoServicesUrl: {
    id: 'legalNotices.videoServicePolicy.url',
    description: 'Video services url',
  },
  californiaPrivacyLabel: {
    id: 'legalNotices.californiaPrivacy.label',
    description: 'California privacy label',
  },
  californiaPrivacyUrl: {
    id: 'legalNotices.californiaPrivacy.url',
    description: 'California privacy url',
  },
  doNotSellMyInfoLabel: {
    id: 'legalNotices.californiaDoNotSell.label',
    description: 'Do not sell label',
  },
  doNotSellMyInfoUrl: {
    id: 'legalNotices.californiaDoNotSell.url',
    description: 'Do not sell url',
  },
  privacyChoicesLabel: {
    id: 'legalNotices.privacyChoices.label',
    description: 'Privacy choices label',
  },
};

const trackLegalNotices = tracker => {
  const pageData = {
    pageType: 'legal',
    ...getPageValues('legal'),
  };

  tracker(pageData);
};

const Fallback = () => {
  const intl = useIntl();

  return (
    <React.Fragment>
      <p>
        <b>{intl.formatMessage(messages.privacyPolicyLabel)}</b>
        <br />
        {intl.formatMessage(messages.privacyPolicyUrl)}
      </p>
      <p>
        <b>{intl.formatMessage(messages.termsOfUseLabel)}</b>
        <br />
        {intl.formatMessage(messages.termsOfUseUrl)}
      </p>
      <p>
        <b>{intl.formatMessage(messages.videoServicesLabel)}</b>
        <br />
        {intl.formatMessage(messages.videoServicesUrl)}
      </p>
      <p>
        <b>{intl.formatMessage(messages.californiaPrivacyLabel)}</b>
        <br />
        {intl.formatMessage(messages.californiaPrivacyUrl)}
      </p>
      <p>
        <b>{intl.formatMessage(messages.doNotSellMyInfoLabel)}</b>
        <br />
        {intl.formatMessage(messages.doNotSellMyInfoUrl)}
      </p>
    </React.Fragment>
  );
};

const LegalNotices = () => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const focusManager = useFocusManager();

  const contentRef = useRef(null);

  const legalLink = useSelector(
    state => state.kitchenSink.data?.settings.legal?.link,
  );

  const location = window.cmp.consent.getConsentModel();

  useEffect(() => {
    dispatch(fetchLegalNotices(legalLink));
  }, [dispatch, legalLink]);

  useEffect(() => {
    focusManager.setCurrentFocus('privacy-choices-button');
  }, [focusManager]);

  const html = useSelector(reduxState => reduxState.legalNotices.data);
  const status = useSelector(reduxState => reduxState.legalNotices.status); // idle, pending, validating, validated, error
  const content = useMemo(() => {
    if (['idle', 'pending'].includes(status)) return null;

    const shouldRenderFallback = status === 'error';

    return shouldRenderFallback ? (
      <div ref={contentRef} className={styles.html}>
        <Fallback />
      </div>
    ) : (
      <div ref={contentRef} className={styles.html}>
        <div
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: html,
          }}
        />
      </div>
    );
  }, [html, status]);

  const legalNoticesData = useSelector(
    state => state.kitchenSink.data.settings?.legal?.label,
  );

  const legalNoticesLabel =
    legalNoticesData || intl.formatMessage(messages.legalNoticesLabel);
  useEffect(() => {
    if (['validated', 'error'].includes(status)) {
      const tts = contentRef.current?.innerText;
      if (tts) {
        textToSpeechManager.add(legalNoticesLabel);
        textToSpeechManager.add(tts.split('\n'));
        textToSpeechManager.add(
          intl.formatMessage(messages.privacyChoicesLabel),
        );
        textToSpeechManager.speak();
      }
    }
  }, [intl, legalNoticesLabel, status]);

  // Validation effect
  useEffect(() => {
    if (contentRef.current && status === 'validating') {
      const { children, childElementCount } = contentRef.current;
      let isRenderable = false;

      if (childElementCount > 0) {
        for (let i = 0; i < childElementCount; i += 1) {
          const child = children[i];
          const bounds = child.getBoundingClientRect();
          if (bounds !== null && bounds.width > 0 && bounds.height > 0) {
            // As soon as one item has bounds, we can expect it has rendered something visually
            isRenderable = true;
            break;
          }
        }
      } else if (!isEmptyOrNull(contentRef.current.innerHTML)) {
        isRenderable = true;
      }

      dispatch(validateLegalNotices(isRenderable));
    }
  }, [status, dispatch]);

  const { trackPage: tracker } = useTracking();
  useEffect(() => {
    trackLegalNotices(tracker);
  }, [tracker]);

  const onPrivacyChoicesClick = () => {
    if (location === 'us_general') {
      history.push({
        pathname: '/privacyUS',
      });
    } else if (location === 'gdpr' || location === 'quebec') {
      history.push({
        pathname: '/privacyGDPR',
      });
    } else {
      // DO NOTHING
    }
  };

  return (
    <Focusable
      focusId="legal-notices-page"
      className={classNames('w-100 h-100 fixed-top', styles.page)}
      data-testid="legal-notices-page"
      render={() => (
        <WithLoading loading={status === 'pending'}>
          <LogoPage className="position-relative">
            <DividedContent
              title={<div className={styles.title}>{legalNoticesLabel}</div>}
            >
              {content}
              <Button
                type="button"
                onClick={onPrivacyChoicesClick}
                focusId="privacy-choices-button"
                className={classNames(styles.button)}
                ttsText={intl.formatMessage(messages.privacyChoicesLabel)}
              >
                <FormattedMessage {...messages.privacyChoicesLabel} />
              </Button>
            </DividedContent>
          </LogoPage>
        </WithLoading>
      )}
    />
  );
};

export default LegalNotices;
